import { useCallback, useContext, useEffect, useState } from 'react'

import { sdk } from '../../config'
import { AuthContext } from '../../providers'
import { VehicleEntry } from '../../sdk'
import { handleSdkError } from '../../utils'
import { loggingAssert } from '../../utils/handleError/loggingAssert'
import useDebounce from '../useDebouncedFn/useDebouncedFn'
import {
  MIN_SEARCH_CHARS,
  useTooFewCharactersTyped
} from '../useTooFewCharactersTyped'

interface Hook {
  matchingVehicles: VehicleEntry[]
  loading: boolean
  errorMessage: string
}

export const useSearchResultsVehicles = (searchInput: string): Hook => {
  const debouncedInput = useDebounce<string>(searchInput)
  const { currentUser, userShopId } = useContext(AuthContext)
  const [loading, setLoading] = useState(false)
  const [matchingVehicles, setMatchingVehicles] = useState<VehicleEntry[]>([])
  const [searchErrorMessage, setSearchErrorMessage] = useState('')

  const inputErrorMessage = useTooFewCharactersTyped(
    searchInput,
    matchingVehicles.length
  )

  const getResultsFn = useCallback(
    async (searchTerm: string) => {
      try {
        const response = await sdk.searchVehicleByKeyword({
          keyword: searchTerm,
          shop: loggingAssert('ShopId not set', userShopId),
          authorization: currentUser?.accessToken as string
        })

        setMatchingVehicles(response)
        setLoading(false)
      } catch (error) {
        const message = (await handleSdkError(error)).message
        setSearchErrorMessage(message)
        setLoading(false)
      }
    },
    [userShopId, currentUser?.accessToken]
  )

  useEffect(() => {
    if (debouncedInput?.length >= MIN_SEARCH_CHARS) {
      getResultsFn(debouncedInput)
    } else {
      setMatchingVehicles([])
    }
  }, [debouncedInput, getResultsFn])

  // display loading indicator as soon as user starts typing even though actual search does not happen until they've typed MIN_SEARCH_CHARS chars (a help message will appear if the user types less than 3 characters)
  useEffect(() => {
    if (searchInput !== '') {
      setLoading(true)
    }
  }, [searchInput])

  useEffect(() => {
    if ((searchErrorMessage || inputErrorMessage) && loading) {
      setLoading(false)
    }
  }, [loading, inputErrorMessage, searchErrorMessage])

  useEffect(() => {
    if (
      matchingVehicles.length > 0 &&
      (inputErrorMessage || searchErrorMessage)
    ) {
      setMatchingVehicles([])
    }
  }, [matchingVehicles, inputErrorMessage, searchErrorMessage])

  return {
    matchingVehicles,
    errorMessage: searchErrorMessage || inputErrorMessage,
    loading
  }
}
